import clsx from 'clsx'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { CustomSelect } from '@wartek-id/select'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import {
  PENGEMBANGAN_KOMPETENSI_ASPECT_LABELS,
  PENGEMBANGAN_KOMPETENSI_ASPECT_INDICATORS,
} from 'app/KinerjaPSPegawaiPage/constants/pengembanganKompetensiV2'
import { useAPIGetPSPegawaiProfile } from 'api/kinerja/pengawas-sekolah/profile'

import type {
  PengembanganKompetensiV2KegiatanAspects,
  PengembanganKompetensiV2KegiatanOption,
} from 'types/kinerja-ps-pegawai'

type FormProps = {
  options: PengembanganKompetensiV2KegiatanAspects
  isLoading: boolean
  isFetched: boolean
}
const Form: React.FC<FormProps> = ({ options, isLoading, isFetched }) => {
  const { control } = useFormContext()
  const pemantauanValue = useWatch({ name: 'pemantauan', control })
  const penilaianValue = useWatch({ name: 'penilaian', control })
  const pembinaanValue = useWatch({ name: 'pembinaan', control })

  const aspectFormStates = {
    pemantauan: pemantauanValue,
    penilaian: penilaianValue,
    pembinaan: pembinaanValue,
  }

  const { data: { data: profilePSPegawai } = {} } = useAPIGetPSPegawaiProfile(
    {}
  )

  function flattenJenjang(data) {
    const result = []

    data.forEach((group) => {
      // Push header row
      result.push({
        id: null,
        name: group.jenjang,
        disabled: true,
      })

      // Push each kegiatan item
      ;(group?.kegiatan ?? []).forEach((item) => {
        result.push({
          id: item.id,
          name: item.name,
        })
      })
    })

    return result
  }

  return (
    <>
      {/* eslint-disable react/no-unknown-property */}
      <style jsx global>
        {`
          .aspect-activities-dropdown li:has(.group-option) {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
            padding-left: 1rem;
            padding-right: 1rem;
            --tw-bg-opacity: 1;
            background-color: rgb(244 244 244 / var(--tw-bg-opacity));
            cursor: not-allowed;
          }
        `}
      </style>

      {isLoading && (
        <div className="flex justify-center w-full">
          <Spinner />
        </div>
      )}

      {isFetched &&
        Object.keys(options ?? {})
          .filter((key) => key !== 'rekomendasi')
          .map((key) => {
            const aspectOptions = flattenJenjang(options?.[key] ?? [])
            const selectedValue =
              aspectOptions.find(({ id }) => id === aspectFormStates?.[key]) ??
              {}

            return (
              <Controller
                key={key}
                control={control}
                name={key}
                rules={{
                  required: 'Wajib diisi.',
                }}
                render={({ field: { onChange }, fieldState: { error } }) => {
                  return (
                    <div className="mb-8 space-y-4">
                      <Text variant="heading-md">
                        Aspek:{' '}
                        {PENGEMBANGAN_KOMPETENSI_ASPECT_LABELS?.[key] ?? '-'}
                      </Text>

                      <div className="p-6 space-y-8 border rounded-md border-subdued">
                        <div>
                          <div className="p-4 border border-subdued bg-surface-subdued rounded-t-md">
                            <Text variant="heading-sm">
                              Indikator perilaku yang perlu anda capai sebagai
                              Pengawas {profilePSPegawai?.jenjangJabatan ?? ''}
                            </Text>
                          </div>
                          <div className="p-4 border bg-surface-selected-default rounded-b-md border-subdued">
                            <ul className="pl-6 list-disc">
                              {(
                                PENGEMBANGAN_KOMPETENSI_ASPECT_INDICATORS?.[
                                  key
                                ]?.[
                                  (
                                    profilePSPegawai?.jenjangJabatan ?? ''
                                  ).toLocaleLowerCase()
                                ] ?? []
                              ).map((indicator, index) => (
                                <li key={`aspect-indicator-${index}`}>
                                  {indicator}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <label htmlFor={`activity-${key}`}>
                            <Text variant="heading-sm">
                              Pilih kegiatan untuk meningkatkan indikator
                              perilaku
                            </Text>
                          </label>

                          <CustomSelect
                            className={clsx(
                              'aspect-activities-dropdown',
                              Boolean(error?.message) &&
                                'border-critical-default'
                            )}
                            data-testid="kegiatan-dropdown"
                            placeholder="Pilih di sini"
                            options={aspectOptions}
                            customList={({ item }) => {
                              if (
                                (item as PengembanganKompetensiV2KegiatanOption)
                                  .disabled
                              ) {
                                return (
                                  <div className="group-option">
                                    <strong>
                                      {
                                        (
                                          item as PengembanganKompetensiV2KegiatanOption
                                        ).name
                                      }
                                    </strong>
                                  </div>
                                )
                              }

                              return (
                                <div className="flex items-center -mt-1">
                                  {
                                    (
                                      item as PengembanganKompetensiV2KegiatanOption
                                    ).name
                                  }
                                </div>
                              )
                            }}
                            onChange={(item) => {
                              onChange(
                                (item as PengembanganKompetensiV2KegiatanOption)
                                  .id
                              )
                            }}
                            value={selectedValue}
                          />
                        </div>
                      </div>
                    </div>
                  )
                }}
              />
            )
          })}
    </>
  )
}

export default Form
